import _classCallCheck from "/home/circleci/project/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/classCallCheck.js";
import _createClass from "/home/circleci/project/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/createClass.js";
import { AsyncSubject, Observable, of } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import config from './config';
import { DriftWebSocketConnection } from './DriftWebSocketConnection';
import { TrafficControlWebSocketConnection } from './TrafficControlWebSocketConnection';
/*
Current limitation that you can only subscribe one function to each message
type per channel. this encourages you to dispatch an action with the payload early and
do all processing in epics. We do this because determining equality of
functions is effectively impossible and so it would be difficult to determine
if a function was being subscribed to the same message many times by different
components. If you choose to subscribe an actual function, be sure that no other
component will try to subscribe to that specific message type with a different
function at the same time. This can be ok for more global events like events on
org-private, but would be inadvisable for events like presence_diff on end user
presence where its possible the user is subscribed to in the sidebar and the context
header and both require different logic. we count the number of listeners on a given
message in order to unsubscribe from channels when no listeners are registered
as a follow up we may let you subscribe multiple actions to one listener as checking
equality of actions is possible.
*/
export var DriftWebSocket = /*#__PURE__*/function () {
  function DriftWebSocket(connections, store) {
    _classCallCheck(this, DriftWebSocket);
    this.connections = connections;
    this.store = store;
    this.topics = {};
    this.channelRefCount = {};
    var connectionNames = Object.keys(connections);
    this.channels = connectionNames.reduce(function (acc, connectionName) {
      acc[connectionName] = {};
      return acc;
    }, {});
    this.topics = connectionNames.reduce(function (acc, connectionName) {
      acc[connectionName] = {};
      return acc;
    }, {});
    this.channelRefCount = connectionNames.reduce(function (acc, connectionName) {
      acc[connectionName] = {};
      return acc;
    }, {});
  }
  _createClass(DriftWebSocket, [{
    key: "getTrafficControlChannel",
    value: function getTrafficControlChannel(channelName) {
      var _a, _b;
      return (_b = (_a = this.store.getState().socket) === null || _a === void 0 ? void 0 : _a.channels) === null || _b === void 0 ? void 0 : _b.find(function (channel) {
        return channel.topic === channelName;
      });
    }
  }, {
    key: "on",
    value: function on(_ref) {
      var _this = this;
      var cluster = _ref.cluster,
        channelName = _ref.channelName,
        messageName = _ref.messageName,
        joinCallback = _ref.joinCallback,
        useTrafficControlChannel = _ref.useTrafficControlChannel;
      if (!this.channels[cluster][channelName]) {
        var trafficControlChannel = useTrafficControlChannel && this.getTrafficControlChannel(channelName);
        if (trafficControlChannel) {
          this.channels[cluster][channelName] = of(trafficControlChannel);
        } else {
          this.channels[cluster][channelName] = this.joinChannel(cluster, channelName, joinCallback);
        }
      }
      return this.channels[cluster][channelName].pipe(mergeMap(function (channel) {
        return new Observable(function (observer) {
          var channelRef = channel.on(messageName, function (payload) {
            return observer.next(payload);
          });
          var channelCounts = _this.channelRefCount[cluster];
          channelCounts[channelName] = channelCounts[channelName] ? channelCounts[channelName] + 1 : 1;
          // Clean up method for observable
          return function () {
            // Guard against going negative
            if (channelCounts[channelName] > 0) {
              channel.off(messageName, channelRef);
              channelCounts[channelName] = channelCounts[channelName] - 1;
              if (channelCounts[channelName] === 0) {
                channel.leave();
                delete _this.channels[cluster][channelName];
                delete channelCounts[channelName];
              }
            }
          };
        });
      }));
    }
  }, {
    key: "subscribeToPush",
    value: function subscribeToPush(_ref2) {
      var _this2 = this;
      var cluster = _ref2.cluster,
        channelName = _ref2.channelName,
        joinCallback = _ref2.joinCallback;
      if (!this.channels[cluster][channelName]) {
        this.channels[cluster][channelName] = this.joinChannel(cluster, channelName, joinCallback);
      }
      return function (messageName, payload) {
        return _this2.channels[cluster][channelName].subscribe(function (channel) {
          _this2.subscribeToTopic(messageName, channelName, cluster);
          channel.push(messageName, payload);
        });
      };
    }
    // Async subjects will only emit a single value
    // and will continue to emit it after completion.
    // This detail is fairly critical to how this class works
  }, {
    key: "joinChannel",
    value: function joinChannel(cluster, channelName, joinCallback) {
      var subject = new AsyncSubject();
      this.connections[cluster].subscribe(function (socket) {
        var channel = socket.channel(channelName);
        subject.next(channel);
        channel.join().receive('ok', function () {
          joinCallback && joinCallback();
          subject.complete();
        }).receive('error', function () {
          subject.complete();
        }).receive('timeout', function () {
          subject.complete();
        });
      });
      return subject;
    }
  }, {
    key: "subscribeToTopic",
    value: function subscribeToTopic(messageName, channelName, cluster) {
      if (!this.topics[cluster][channelName] || this.topics[cluster][channelName] && !this.topics[cluster][channelName][messageName]) {
        if (!this.topics[cluster][channelName]) {
          this.topics[cluster][channelName] = {};
        }
        this.topics[cluster][channelName][messageName] = 1;
      }
      this.topics[cluster][channelName][messageName] += 1;
    }
  }]);
  return DriftWebSocket;
}();
var singleton;
export var initializeWebSocket = function initializeWebSocket(orgId, store) {
  var includePushCluster = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  if (!singleton) {
    var connections = {
      chat: new TrafficControlWebSocketConnection(store)
    };
    if (includePushCluster) {
      connections.push = new DriftWebSocketConnection(config.PUSH_WS, 'push', orgId, store);
    }
    singleton = new DriftWebSocket(connections, store);
  }
};
export var getDriftWebSocket = function getDriftWebSocket() {
  if (!singleton) {
    throw new Error('Attempted to get websocket before initialization');
  }
  return singleton;
};